
import { useState } from "react";
import { Editor } from "@tiptap/react";
import { ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { FileObject } from "@supabase/storage-js";

interface ImageUploadControlsProps {
  editor: Editor;
}

export const ImageUploadControls = ({ editor }: ImageUploadControlsProps) => {
  const [imageUrl, setImageUrl] = useState("");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<FileObject[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchGalleryImages = async () => {
    setLoading(true);
    try {
      const { data: files, error } = await supabase
        .storage
        .from('article-images')
        .list();

      if (error) {
        throw error;
      }
      setGalleryImages(files);
    } catch (error) {
      console.error("Error fetching gallery images:", error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (fileName: string) => {
    const { data } = supabase.storage
      .from('article-images')
      .getPublicUrl(fileName);
    return data.publicUrl;
  };

  const getDefaultImageStyle = () => {
    return 'width: 50%; max-width: 100%; height: auto; border-radius: 8px; margin-bottom: 16px; float: left; margin-right: 16px; clear: left;';
  };

  const addImage = (url?: string) => {
    const imageUrlToAdd = url || imageUrl;
    if (!imageUrlToAdd || !editor) return;

    console.log("[ImageUploadControls] Adding image to editor:", imageUrlToAdd);
    
    // Insert image with basic attributes first
    editor.chain().focus().setImage({ 
      src: imageUrlToAdd,
      alt: 'Newsletter image'
    }).run();
    
    // Then update the attributes to include style and class
    setTimeout(() => {
      editor.chain().focus().updateAttributes('image', { 
        style: getDefaultImageStyle(),
        class: 'newsletter-image'
      }).run();
    }, 10);
    
    setImageUrl("");
    setGalleryOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addImage();
    }
  };

  return (
    <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
      <div className="flex gap-2 items-center">
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => fetchGalleryImages()}>
            <ImageIcon className="h-4 w-4" />
            Choose from Gallery
          </Button>
        </DialogTrigger>
        <div className="flex gap-2 items-center">
          <Input
            type="text"
            placeholder="or paste image URL"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-8 w-48"
          />
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => addImage()}
            disabled={!imageUrl}
            className="gap-1"
          >
            <ImageIcon className="h-3 w-3" />
            Insert
          </Button>
        </div>
      </div>
      
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Insert Image from Gallery</DialogTitle>
          <DialogDescription>
            Select an image from your gallery to insert into the newsletter
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="p-8 text-center">Loading images...</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4">
            {galleryImages.map((image) => (
              <div 
                key={image.id} 
                className="relative group aspect-square overflow-hidden rounded-md border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors"
                onClick={() => addImage(getImageUrl(image.name))}
              >
                <img
                  src={getImageUrl(image.name)}
                  alt={image.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    Insert Image
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {galleryImages.length === 0 && !loading && (
          <div className="p-8 text-center text-gray-500">
            No images found in your gallery
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
